import React, { useEffect } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../seo";

const featureCards = [
  {
    href: "/features/chatbot-builder",
    title: "AI Chatbot Builder",
    description:
      "Build a smart FAQ-powered AI chatbot in minutes. Customize your persona, branding, and responses — no coding required.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
  },
  {
    href: "/features/no-code",
    title: "No-Code Setup",
    description:
      "Upload your FAQs, tweak your bot, embed one script tag. Go live in under 5 minutes — zero technical skills needed.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    href: "/features/integrations",
    title: "Integrations",
    description:
      "Works with Shopify, WordPress, Wix, and any custom site. Connect to your CRM or helpdesk via the embeddable agent widget.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
  },
  {
    href: "/features/multilingual",
    title: "Multilingual Support",
    description:
      "Serve customers in English, Bahasa Melayu, and 中文. The AI automatically replies in the language your customer uses.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
  },
];

export function FeaturesPage() {
  useEffect(() => {
    document.title = "Onyx AI Features — AI Chatbot Platform";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Explore Onyx AI features: multilingual chatbot, no-code builder, integrations, live agent handoff & more."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/features");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Features", path: "/features" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#8a7a9a]">Features</span>
          </nav>

          <div className="text-center mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              Platform Features
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Everything You Need to Automate Support
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl mx-auto">
              A complete AI chatbot platform built for Malaysian businesses — from kedai runcit to enterprise. Explore each feature in detail below.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {featureCards.map((card) => (
              <a
                key={card.href}
                href={card.href}
                className="group p-7 rounded-2xl onyx-card onyx-glow-hover flex flex-col gap-4 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0] group-hover:bg-[#a078d0]/20 transition-colors">
                  {card.icon}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                    {card.title}
                    <svg
                      className="w-4 h-4 text-[#a078d0] opacity-0 group-hover:opacity-100 transition-all -translate-x-1 group-hover:translate-x-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </h2>
                  <p className="text-[#8a7a9a] text-sm leading-relaxed">{card.description}</p>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-20 text-center">
            <p className="text-[#8a7a9a] mb-5 text-lg">
              Ready to put these features to work for your business?
            </p>
            <a
              href="/signup"
              className="inline-block px-8 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
